/* One scroll-driven emblem per empty product panel, including merged fields. */
(() => {
  const main = document.querySelector('#MainContent');
  // This controller only belongs to collection lists. Do not update
  // inherited page-wide variables on a homepage with no collection panels.
  if (!main || (document.body.classList.contains('ak-page-index') && !main.querySelector('#filterable'))) return;
  const desktop = matchMedia('(min-width:1201px)');
  const reduced = matchMedia('(prefers-reduced-motion:reduce)');
  const panels = new WeakSet();
  let scheduled = false;
  let idleResume = 0;
  const path = 'M462.495000 219.889374L323.632122 219.889374L421.821587 121.699910L378.815090 78.693413L280.625626 176.882878L280.625626 38.020000L219.804374 38.020000L219.804374 176.882878L121.614910 78.693413L78.608413 121.699910L176.797878 219.889374L37.935000 219.889374L37.935000 280.710626L176.797878 280.710626L78.608413 378.900090L121.614910 421.906587L219.804374 323.717122L219.804374 462.580000L280.625626 462.580000L280.625626 323.717122L378.815090 421.906587L421.821587 378.900090L323.632122 280.710626L462.495000 280.710626L462.495000 219.889374Z';
  function size(panel) {
    const box = panel.getBoundingClientRect();
    const list = main.querySelector('.collection-list')?.getBoundingClientRect();
    const paintedLines = [...document.querySelectorAll('.ak-single-level-menu-master-line')]
      .map(line => line.getBoundingClientRect()).filter(line => line.width > 0)
      .map(line => line.left + line.width / 2);
    // Short menus expose only some of the eight master fields. Keep the
    // complete lattice, replacing visible boundaries by their painted centre.
    const masterLines = list ? Array.from({length:7}, (_, i) => {
      const expected = list.left + list.width * (i + 1) / 8 + .5;
      return paintedLines.find(x => Math.abs(x - expected) < 2) ?? expected;
    }) : paintedLines;
    if (box.width > 0 && masterLines.length) {
      const centre = box.left + box.width / 2;
      const target = masterLines.reduce((a, b) => Math.abs(a - centre) <= Math.abs(b - centre) ? a : b);
      // SVG strokes paint half a pixel either side of their coordinates;
      // use the actual menu stroke centre, not its geometric left edge.
      const local = target - box.left;
      panel.style.setProperty('--ak-hatch-emblem-x', `${local}px`);
      const cross = panel.querySelector('.ak-hatch-cross path');
      const path = `M${local / box.width * 100} 0V100M0 50H100`;
      if (cross && cross.getAttribute('d') !== path) cross.setAttribute('d', path);
    }
    const diameter = Math.min(panel.clientWidth, panel.clientHeight) * .55;
    const value = `${diameter}px`;
    if (panel.style.getPropertyValue('--ak-hatch-emblem-diameter') !== value) {
      panel.style.setProperty('--ak-hatch-emblem-diameter', value);
    }
  }
  const sizes = new ResizeObserver(entries => entries.forEach(entry => size(entry.target)));
  function discover() {
    main.querySelectorAll('.ak-collection-grid-tile .ak-collection-grid-panel').forEach(panel => {
      if (panels.has(panel)) return;
      panels.add(panel);
      // The crossed background spans the complete field, including merged fields.
      if (!panel.querySelector('.ak-hatch-cross')) {
        const cross = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        cross.classList.add('ak-hatch-cross');
        cross.setAttribute('viewBox', '0 0 100 100');
        cross.setAttribute('preserveAspectRatio', 'none');
        cross.setAttribute('aria-hidden', 'true');
        cross.setAttribute('focusable', 'false');
        const diagonals = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        diagonals.setAttribute('d', 'M50 0V100M0 50H100');
        diagonals.setAttribute('vector-effect', 'non-scaling-stroke');
        cross.append(diagonals);
        panel.prepend(cross);
      }
      // Row planning may clone a panel that already contains the emblem.
      if (!panel.querySelector('.ak-hatch-scroll-emblem')) {
        const circle = document.createElement('span');
        circle.className = 'ak-hatch-scroll-emblem';
        circle.setAttribute('aria-hidden', 'true');
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '37.71 38.02 425.01 424.56');
        icon.setAttribute('focusable', 'false');
        const mark = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        mark.setAttribute('d', path);
        icon.append(mark);
        circle.append(icon);
        panel.append(circle);
      }
      sizes.observe(panel);
      size(panel);
    });
    update();
  }
  function update() {
    scheduled = false;
    const offset = Math.max(window.scrollY, document.scrollingElement?.scrollTop || 0, main.scrollTop);
    main.style.setProperty('--ak-hatch-scroll-angle', `${desktop.matches && !reduced.matches ? offset * .18 : 0}deg`);
  }
  function queue() {
    main.classList.add('ak-hatch-scrolling');
    clearTimeout(idleResume);
    idleResume = setTimeout(() => main.classList.remove('ak-hatch-scrolling'), 160);
    if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
  }
  const list = main.querySelector('#filterable');
  if (list) new MutationObserver(discover).observe(list, {childList:true, subtree:true});
  document.addEventListener('scroll', queue, {capture:true, passive:true});
  window.addEventListener('scroll', queue, {passive:true});
  window.addEventListener('ak:browser-resume',()=>{scheduled=false;clearTimeout(idleResume);main.classList.remove('ak-hatch-scrolling');discover();});
  window.addEventListener('pageshow', () => {
    clearTimeout(idleResume);
    main.classList.remove('ak-hatch-scrolling');
    discover();
  });
  let layoutFrame = 0;
  function layout() {
    if (layoutFrame) return;
    layoutFrame = requestAnimationFrame(() => {
      layoutFrame = 0;
      main.querySelectorAll('.ak-collection-grid-panel').forEach(size);
    });
  }
  window.addEventListener('resize', layout, {passive:true});
  ['ak:global-frame-ready','ak:page-enter','ak:prepare-page-reveal'].forEach(name => window.addEventListener(name, layout));
  desktop.addEventListener('change', discover);
  reduced.addEventListener('change', queue);
  document.addEventListener('shopify:section:load', discover);
  discover();
})();
